import { React, useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Button, Text, MTable, Tooltip, Copy, DeviceStatus, Popover, MLink, XCircle, Search } from "@cisco/spaces-dash-lib-styleguide";
import { NodeContext } from "../../contexts/nodeContext";
import CameraEmpty from "../../assets/images/location-accesspoints/camera_empty_lh2.svg";
import * as BusinessHierarchyService from "../../service/business-hierarchy-service";
import { I18nContext } from "../../contexts/i18nContext";
import CustomSearch from "../custom-search/custom-search";
import useLoader from "../../hooks/useLoader";
import "../../common/common_style.scss";
import "../../common/global_style.scss";
import "./location-webex.scss";

function LocationWebex(props) {
  const i18n = useContext(I18nContext);
  const navigate = useNavigate();
  const { node, webexCount, locationRowMap } = useContext(NodeContext);
  const [locationNode] = useState({ ...node });
  const [webexListState, setWebexListState] = useState([]);
  const [webexListPagedState, setWebexListPagedState] = useState([]);
  const [searchText, setSearchText] = useState("");
  const { setLoading } = useLoader();
  const [textColor, setTextColor] = useState("#f7f7f7");
  const [bgColor, setBgColor] = useState({ background: "#656c75" });
  const rsearchText = useRef("");
  const rtotalNoOfItems = useRef(webexCount);
  const [showTable, setShowTable] = useState(false);
  const [copyIcon, setCopyIcon] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const hoveredItemId = useRef(null);

  let webexList = [];
  let webexListFILTERED = [];
  let webexListPaged = [];
  const appUserSearchSkeleton = {
    displayName: "string",
    macAddress: "string"
  };

  const filterValue = (event) => {
    setSearchText(event.target.value);
    rsearchText.current = event.target.value;
    setShowTable(true);
  };

  const getLocationPath = (apId) => {
    try {
      let locNameArr = [];
      const parentLoc = locationRowMap[apId];
      if (!parentLoc) {
        return "";
      }
      const locPathIds = parentLoc.locationPath;
      if (locPathIds && locPathIds.length) {
        locNameArr = locPathIds
          .map((id) => {
            const loc = locationRowMap[id];
            return loc ? loc.name : null;
          })
          .filter((name) => name !== null);
      }
      const locNameStr = locNameArr.length ? locNameArr.join(" > ") : "";
      return locNameStr;
    } catch (e) {
      return "";
    }
  };

  const getLocationPathTruncated = (apId) => {
    const locPath = getLocationPath(apId);
    if (locPath.indexOf(" > ") - 1) {
      const count = locPath.split(" > ").length - 1;
      if (count > 1) {
        const arr = locPath.split(" > ");
        const newLocPathArr = [];
        newLocPathArr.push(arr[0]);
        newLocPathArr.push(arr[arr.length - 1]);
        const newLocPath = newLocPathArr.join(" > ... > ");
        return newLocPath;
      }
      return locPath;
    }
    return locPath;
  };

  const copyLocationPath = (locationPath) => {
    navigator.clipboard.writeText(locationPath);
    setIsCopied(true);
    setBgColor({
      ...bgColor,
      background: "#FFFF"
    });
    setTextColor("#545454");
  };
  const getCellValueWithToolTip = ({ row }) => {
    return (
      <div
        onMouseEnter={(val) => {
          console.log(val.currentTarget.attributes.getNamedItem("id").value);
          hoveredItemId.current = val.currentTarget.attributes.getNamedItem("id").value;
          setCopyIcon(true);
        }}
        onMouseLeave={() => {
          setIsCopied(false);
          setCopyIcon(false);
          setBgColor({
            ...bgColor,
            background: "#656c75"
          });
          setTextColor("#f7f7f7");
        }}
        id={row.id}
      >
        <Flex direction="horizontal" gap={2} align="center" justify="flex-start">
          <Tooltip title={row.original.locationPath} placement="bottom">
            <span className="hover-target">{row.original.locationPathTruncated}</span>
          </Tooltip>
          <Popover
            style={bgColor}
            placement="top"
            kind={isCopied ? "default" : "map"}
            title={
              <Flex direction="horizontal" gap={8} align="center" justify="space-between">
                {isCopied && (
                  <>
                    <DeviceStatus status="good" />
                    <Text color={textColor} size="p4" weight="regular">
                      {i18n.t("hierarchy-base.copied-to-clipboard")}
                    </Text>
                  </>
                )}
                {!isCopied && (
                  <Text color={textColor} size="p4" weight="regular">
                    {i18n.t("hierarchy-base.copy-to-clipboard")}
                  </Text>
                )}
              </Flex>
            }
            visible
          >
            <span className="hover-target">
              <Button
                icon={
                  <Copy
                    size={15}
                    onClick={() => {
                      copyLocationPath(row.original.locationPath);
                    }}
                  />
                }
                kind="tertiary"
                size="sm"
              />
            </span>
          </Popover>
        </Flex>
      </div>
    );
  };

  const searchWebexList = (data) => {
    const tempApList = data;
    if (tempApList && tempApList.length > 0) {
      setShowTable(true);
    }
    webexList = tempApList;
    setWebexListState(tempApList);
    webexListFILTERED = webexList.map((webex) => ({
      ...webex,
      locationPath: getLocationPath(webex?.locationId),
      locationPathTruncated: getLocationPathTruncated(webex?.locationId)
    }));
  };

  const refreshPaginator = () => {
    webexListPaged = webexListFILTERED;
    setWebexListPagedState(webexListPaged);
  };

  const navigateToWebex = () => {
    navigate("/setup/webex");
  };

  const processWebexListResponse = (response) => {
    if (response.status) {
      const tempApList = response.data ? response.data : [];
      if (tempApList && tempApList.length > 0) {
        setShowTable(true);
      }
      webexList = tempApList;
      setWebexListState(tempApList);
    } else {
      webexList = [];
      setWebexListState([]);
    }
    webexListFILTERED = webexList.map((webex) => ({
      ...webex,
      locationPath: getLocationPath(webex?.locationId),
      locationPathTruncated: getLocationPathTruncated(webex?.locationId)
    }));
  };

  const initWebexList = (currentPageNo = 1) => {
    setLoading(true);
    BusinessHierarchyService.getWebexDevicesList(locationNode.id)
      .then((response) => {
        setLoading(false);
        processWebexListResponse(response);
        if (currentPageNo == 1) {
          refreshPaginator();
        } else {
          setWebexListPagedState(webexListFILTERED);
        }
      })
      .catch((error) => {
        setLoading(false);
        webexList = [];
        setWebexListState([]);
        webexListFILTERED = [];
        refreshPaginator();
        console.log(`Location Webex Devices: Error on getwebexList${error}`);
      });
  };

  const getwebexList = (currentPageNo = 1) => {
    setLoading(true);
    BusinessHierarchyService.getWebexDevicesList(locationNode.id)
      .then((response) => {
        setLoading(false);
        processWebexListResponse(response);
        if (currentPageNo == 1) {
          refreshPaginator();
        } else {
          setWebexListPagedState(webexListFILTERED);
        }
      })
      .catch((error) => {
        setLoading(false);
        webexList = [];
        setWebexListState([]);
        webexListFILTERED = [];
        refreshPaginator();
        console.log(`Location Webex Devices: Error on getwebexList${error}`);
      });
  };

  const clearSearch = () => {
    setSearchText("");
    rsearchText.current = "";
    rtotalNoOfItems.current = webexCount;
    setShowTable(true);
    getwebexList();
  };

  const onAdminSearchFilter = (data) => {
    console.log(data);
    if (rsearchText.current.length > 0) {
      setShowTable(true);
      rtotalNoOfItems.current = data.length;
      searchWebexList(data);
      refreshPaginator();
    } else {
      setShowTable(true);
      rtotalNoOfItems.current = webexCount;
      getwebexList();
    }
  };

  useEffect(() => {
    console.log("LocationWebex Mount");
    console.log(`${props} : ${copyIcon}`);
    if (webexCount && webexCount > 0) {
      initWebexList();
    }

    return () => {
      console.log("LocationWebex Destroy");
    };
  }, []);

  return (
    <div className="ap-wrapper boxShadowBlur" id="webex-wrapper">
      <Flex direction="vertical" gap={16}>
        <Flex align="center" justify="space-between" gap={8} />
        {showTable && webexListPagedState && webexListPagedState.length >= 0 && (
          <Flex align="center" justify="space-between">
            <Flex justify="space-between">
              <Flex direction="vertical">
                <Search
                  label=""
                  placeholder={i18n.t("proximity-rule.search-text")}
                  options={[]}
                  onChange={filterValue}
                  value={searchText}
                  suffix={searchText.length > 0 && <XCircle title="clear search" size={18} onClick={clearSearch} />}
                />
                <CustomSearch
                  searchSkeleton={appUserSearchSkeleton}
                  customSearchText={searchText.toLowerCase()}
                  shouldUseGlobalSearch={false}
                  rawData={webexListState}
                  onAdminSearchFilter={onAdminSearchFilter}
                />
              </Flex>
            </Flex>
            <Flex justify="space-between">
              <Flex align="center">
                {showTable && webexListPagedState && webexListPagedState.length > 0 && (
                  <MLink onClick={navigateToWebex} size="md">
                    {i18n.t("location-webex.onboard-webex-devices")}
                  </MLink>
                )}
              </Flex>
            </Flex>
          </Flex>
        )}
        {showTable && webexListPagedState && webexListPagedState.length >= 0 && (
          <div className="userTableContainer">
            <MTable.Pagination key={webexListPagedState.length} currentPage={1} defaultRecordsOption="10" showNavigation="all" showTotal showOptions>
              <MTable
                columns={[
                  {
                    accessorKey: "displayName",
                    header: i18n.t("location-webex.name-text"),
                    meta: {
                      align: "left"
                    },
                    enableSorting: true
                  },
                  {
                    accessorKey: "macAddress",
                    header: i18n.t("business-location-details.mac-address-text"),
                    meta: {
                      align: "left"
                    },
                    enableSorting: true
                  },
                  {
                    accessorKey: "locationPathTruncated",
                    cell: getCellValueWithToolTip,
                    header: i18n.t("location-access-points.location-text"),
                    meta: {
                      align: "left"
                    }
                  }
                ]}
                data={webexListPagedState}
              />
            </MTable.Pagination>
          </div>
        )}
        {!showTable && (!rsearchText.current || rsearchText.current.length == 0) && webexListPagedState.length == 0 && (
          <div className="team-empty-business">
            <div className="divTable empty_wrapper" id="network-webex-texts">
              <div className="divTableRow">
                <div className="divTableCell width50_middle">
                  <div className="title">
                    <Text size="p1" weight="bold">
                      {i18n.t("location-webex.no-webex-for-location")}
                    </Text>
                  </div>
                  <div className="desc">
                    <Text size="p2" weight="regular" color="light">
                      {i18n.t("location-webex.configure-webex-text")}
                    </Text>
                  </div>
                  <Button type="primary" onClick={navigateToWebex}>
                    {i18n.t("location-webex.onboard-webex-devices")}
                  </Button>
                </div>
                <div className="divTableCell width50_middle">
                  <div>
                    <img alt="switches missing" className="team-empty-img" src={CameraEmpty} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Flex>
    </div>
  );
}
export default LocationWebex;
